<!-- FEED SEARCH + FILTERS -->
<div class="cd-card feed-filters">
  <div class="feed-filters-bar">
    <div class="cd-filter-field feed-search-field">
      <label class="cd-filter-label" for="feed-search">Search posts</label>
      <input
        id="feed-search"
        class="cd-filter-input"
        type="text"
        placeholder="Search posts…"
        [ngModel]="searchQuery"
        (ngModelChange)="onSearchInput($event)"
        (keydown.escape)="onSearchInput('')"
        aria-label="Search posts by content"
        autocomplete="off"
        data-testid="feed-search-input"
      />
    </div>

    @if (canViewOwnPosts) {
      <div class="feed-mode-toggle" role="group" aria-label="Choose which posts to show">
        <button
          type="button"
          class="feed-filters-toggle"
          [class.active]="feedMode() === 'all'"
          [attr.aria-pressed]="feedMode() === 'all'"
          (click)="setFeedMode('all')"
          data-testid="feed-mode-all-button"
        >
          All posts
        </button>
        <button
          type="button"
          class="feed-filters-toggle"
          [class.active]="feedMode() === 'mine'"
          [attr.aria-pressed]="feedMode() === 'mine'"
          (click)="setFeedMode('mine')"
          data-testid="feed-mode-mine-button"
        >
          My posts
        </button>
      </div>
    }

    @if (careers().length || interests().length) {
      <button
        type="button"
        class="feed-filters-toggle"
        (click)="toggleFiltersOpen()"
        [attr.aria-expanded]="filtersOpen()"
        data-testid="toggle-filters-button"
      >
        <mat-icon aria-hidden="true">tune</mat-icon>
        Filters
        @if (hasActiveFilters) {
          <span class="feed-filters-badge" aria-label="Filters active"></span>
        }
        <mat-icon aria-hidden="true">{{ filtersOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
    }

    @if (canCreatePost) {
      <button
        type="button"
        class="feed-create-post-btn"
        (click)="openCreatePostDialog()"
        aria-haspopup="dialog"
        data-testid="create-post-button"
      >
        <mat-icon aria-hidden="true">add</mat-icon>
        Create Post
      </button>
    }
  </div>

  @if (filtersOpen()) {
    <div class="feed-filters-panel" role="region" aria-label="Filter posts" @slideDown>
      @if (careers().length) {
        <div class="feed-filter-group">
          <span class="feed-filter-label">Career</span>
          <mat-chip-listbox
            multiple
            aria-label="Filter by career"
            [value]="selectedCareerIdsArray()"
            (change)="onCareerFilterChange($event)"
          >
            @for (career of careers(); track career.id) {
              <mat-chip-option
                [value]="career.id"
                [attr.data-testid]="'career-filter-' + career.id"
              >
                {{ career.name }}
              </mat-chip-option>
            }
          </mat-chip-listbox>
        </div>
      }

      @if (interests().length) {
        <div class="feed-filter-group">
          <span class="feed-filter-label">Interest</span>
          <mat-chip-listbox
            multiple
            aria-label="Filter by interest"
            [value]="selectedInterestIdsArray()"
            (change)="onInterestFilterChange($event)"
          >
            @for (interest of interests(); track interest.id) {
              <mat-chip-option
                [value]="interest.id"
                [attr.data-testid]="'interest-filter-' + interest.id"
              >
                {{ interest.name }}
              </mat-chip-option>
            }
          </mat-chip-listbox>
        </div>
      }

      @if (hasActiveFilters) {
        <button
          mat-button
          class="feed-filter-clear"
          (click)="clearFilters()"
          data-testid="clear-filters-button"
        >
          Clear filters
        </button>
      }
    </div>
  }
</div>

<!-- POST: COUNSELOR -->
@for (post of posts(); track post.id) {
  <mat-card class="post-card" @fadeIn>
    <mat-card-content class="hm-post">
      <div class="hm-post-header">
        <app-user-link [userId]="post.user?.id" [decorative]="true">
          <div class="hm-post-avatar amber" aria-hidden="true">
            <app-user-avatar
              [imageUrl]="post.user?.profilePicturePath"
              [name]="(post.user?.preferredName || post.user?.firstName) + ' ' + post.user?.lastName"
            />
          </div>
        </app-user-link>
        <div class="hm-post-meta">
          <h4>
            @if (post.user?.userType === 'student') {
              <app-user-link [userId]="post.user?.id">{{
                (post.user?.preferredName || post.user?.firstName) + ' ' + post.user?.lastName
              }}</app-user-link>
              <mat-chip class="pill pill-blue">{{ isOwnPost(post) ? 'You' : 'Student' }}</mat-chip>
            } @else {
              <app-user-link [userId]="post.user?.id">{{ post.user?.lastName }}</app-user-link>
              <mat-chip class="pill pill-blue">Counselor</mat-chip>
            }
            @if (post.status === 'reported' && isOwnPost(post)) {
              <mat-chip class="pill" data-testid="post-under-review-chip">Under review</mat-chip>
            }
          </h4>
          <p data-testid="post-meta">
            @if (post.user?.schoolName) {
              {{ post.user?.schoolName }} ·
            }
            @if (isValidDate(post.date)) {
              <time
                [attr.datetime]="post.date.toISOString()"
                [attr.title]="post.date.toLocaleString()"
                >{{ timeAgo(post.date) }}</time
              >
            }
          </p>
        </div>
      </div>
      <div class="hm-post-body" [innerHTML]="post.content"></div>
      <app-linked-events [events]="post.linkedEvents" calendarPath="/students/calendar" />

      <div class="hm-reaction-row">
        <span class="hm-ml-auto">{{ `${post.commentCount} comments` }}</span>
      </div>
      <mat-divider></mat-divider>
      <mat-card-actions class="hm-post-actions">
        <button
          mat-button
          class="hm-post-action"
          [attr.aria-label]="'Comment on ' + post.user?.lastName + 's post'"
          (click)="toggleComments(post.id)"
        >
          <mat-icon aria-hidden="true">chat_bubble_outline</mat-icon> Comment
        </button>
        @if (post.user?.id !== userService.currentUser?.counselorUserId && !isOwnPost(post)) {
          <button
            mat-button
            class="hm-post-action report-action"
            data-testid="report-post-button"
            [attr.aria-label]="'Report ' + post.user?.lastName + 's post'"
            (click)="reportPost(post.id)"
          >
            <mat-icon aria-hidden="true">flag</mat-icon> Report
          </button>
        }
        <button
          mat-button
          class="hm-post-action"
          data-testid="hide-post-button"
          [attr.aria-label]="'Hide ' + post.user?.lastName + 's post'"
          (click)="hidePost(post.id)"
        >
          <mat-icon aria-hidden="true">visibility_off</mat-icon> Hide
        </button>
      </mat-card-actions>

      @if (openCommentsPostId === post.id) {
        <div
          class="comments-section"
          role="region"
          [attr.aria-label]="'Comments on ' + post.user?.lastName + 's post'"
        >
          <div class="comment-input-row">
            <mat-form-field class="comment-input" appearance="outline">
              <input
                matInput
                placeholder="Write a comment..."
                [(ngModel)]="newComment[post.id]"
                [attr.aria-label]="'Write a comment on ' + post.user?.lastName + 's post'"
              />
            </mat-form-field>
            <button
              mat-icon-button
              class="comment-submit-btn"
              (click)="addComment(post.id)"
              aria-label="Submit comment"
            >
              <mat-icon aria-hidden="true">arrow_upward</mat-icon>
            </button>
          </div>

          @for (comment of comments[post.id]; track comment.id) {
            <div class="comment">
              <div class="comment-header">
                <div class="comment-content">
                  <strong
                    ><app-user-link [userId]="comment.user?.id">{{
                      (comment.user?.preferredName || comment.user?.firstName) +
                        ' ' +
                        comment.user?.lastName
                    }}</app-user-link></strong
                  >
                  @if (comment.user?.id === post.user?.id) {
                    <span class="op-badge" aria-label="Original poster">OP</span>
                  }
                  <p>{{ comment.content }}</p>
                </div>
                <button
                  mat-icon-button
                  class="comment-menu-btn"
                  [matMenuTriggerFor]="commentMenu"
                  [attr.aria-label]="'More options for ' + comment.user?.firstName + '\'s comment'"
                >
                  <mat-icon aria-hidden="true">more_vert</mat-icon>
                </button>
                <mat-menu #commentMenu="matMenu">
                  @if (comment.user?.id !== userService.currentUser?.counselorUserId) {
                    <button
                      data-testid="report-comment-button"
                      mat-menu-item
                      (click)="reportComment(comment.id, post.id)"
                    >
                      <mat-icon aria-hidden="true">flag</mat-icon>
                      <span>Report</span>
                    </button>
                  }
                  <button
                    mat-menu-item
                    data-testid="hide-comment-button"
                    (click)="hideComment(comment.id, post.id)"
                  >
                    <mat-icon aria-hidden="true">visibility_off</mat-icon>
                    <span>Hide</span>
                  </button>
                </mat-menu>
              </div>

              <button
                mat-button
                [attr.aria-label]="
                  'Reply to ' +
                  (comment.user?.preferredName || comment.user?.firstName) +
                  ' ' +
                  comment.user?.lastName
                "
                (click)="toggleReply(comment.id)"
              >
                Reply
              </button>

              @if (activeReplyId === comment.id) {
                <div class="comment-input-row">
                  <mat-form-field class="comment-input" appearance="outline">
                    <input
                      matInput
                      [(ngModel)]="replyText[comment.id]"
                      placeholder="Write a reply..."
                      [attr.aria-label]="
                        'Write a reply to ' +
                        (comment.user?.preferredName || comment.user?.firstName) +
                        ' ' +
                        comment.user?.lastName
                      "
                    />
                  </mat-form-field>
                  <button
                    mat-icon-button
                    class="comment-submit-btn"
                    aria-label="Submit reply"
                    (click)="addReply(post.id, comment.id)"
                  >
                    <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                  </button>
                </div>
              }
              @if (comment.replies.length) {
                <div class="replies">
                  @for (reply of comment.replies; track reply.id) {
                    <div class="reply">
                      <strong
                        ><app-user-link [userId]="reply.user?.id">{{
                          (reply.user?.preferredName || reply.user?.firstName) +
                            ' ' +
                            reply.user?.lastName
                        }}</app-user-link></strong
                      >
                      @if (reply.user?.id === post.user?.id) {
                        <span class="op-badge">OP</span>
                      }
                      <p>{{ reply.content }}</p>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </mat-card-content>
  </mat-card>
}
@if (hasMore()) {
  <button class="cd-btn-primary" (click)="loadMore()">Load more</button>
}
@if (isLoading()) {
  <div class="cd-load-spinner">Loading...</div>
}

@if (!hasMore() && posts().length) {
  <div class="cd-load-spinner">You're all caught up</div>
}

@if (feedMode() === 'mine' && !isLoading() && !posts().length) {
  <div class="cd-load-spinner" data-testid="my-posts-empty">You haven't posted anything yet.</div>
}
